
import React, { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trackEvent, trackPageView, updateAnalyticsConsent } from '@/utils/tagManagerUtils';
import { Link } from 'react-router-dom';

// Define the consent options and their default state
export interface ConsentState {
  necessary: boolean;
  analytics: boolean;
}

// The default state - necessary cookies are always required
const defaultConsentState: ConsentState = {
  necessary: true, // Always required
  analytics: false,
};

const CONSENT_STORAGE_KEY = 'upfixe-cookie-consent';

export const CookieConsent: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [consentState, setConsentState] = useState<ConsentState>(defaultConsentState);
  
  // Check if consent has been given before
  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    
    if (!storedConsent) {
      // No consent stored, show the popup
      setOpen(true);
    } else {
      // Consent already given, apply the stored settings
      try {
        const parsedConsent = JSON.parse(storedConsent) as ConsentState;
        setConsentState(parsedConsent);
      } catch (e) {
        console.error('Failed to parse stored consent:', e);
        setOpen(true);
      }
    }
  }, []);
  
  // Save consent to localStorage and apply settings
  const saveConsent = (consent: ConsentState) => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    setConsentState(consent);
    void applyConsentSettings(consent);
    setOpen(false);
    
    // Track the consent action
    trackEvent('cookie_consent_updated', {
      analytics: consent.analytics,
    });
  };
  
  // Apply the consent settings to the page
  const applyConsentSettings = async (consent: ConsentState) => {
    await updateAnalyticsConsent(consent.analytics);

    if (consent.analytics) {
      trackPageView();
    }
  };
  
  // Accept all cookies
  const acceptAll = () => {
    const fullConsent: ConsentState = {
      necessary: true,
      analytics: true,
    };
    saveConsent(fullConsent);
  };
  
  // Accept only necessary cookies
  const acceptNecessary = () => {
    saveConsent(defaultConsentState);
  };
  
  // Handle checkbox changes
  const handleConsentChange = (type: keyof ConsentState) => {
    if (type === 'necessary') return; // Cannot change necessary cookies
    
    setConsentState(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };
  
  // Open the preferences dialog manually (used for privacy policy links)
  const openPreferences = () => {
    setOpen(true);
  };
  
  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
            <DialogDescription>
              We use cookies to enhance your browsing experience and analyze our traffic.
              By clicking "Accept All", you consent to our use of analytics cookies.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="bg-muted p-3 rounded-md flex items-start justify-between">
              <div>
                <h4 className="text-sm font-medium mb-1">Necessary Cookies</h4>
                <p className="text-xs text-muted-foreground">
                  Essential for the website to function. Cannot be disabled.
                </p>
              </div>
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4"
                  checked={true}
                  disabled
                  aria-label="Necessary cookies"
                />
              </div>
            </div>
            
            <div className="bg-muted p-3 rounded-md flex items-start justify-between">
              <div>
                <h4 className="text-sm font-medium mb-1">Analytics Cookies</h4>
                <p className="text-xs text-muted-foreground">
                  Help us understand how visitors interact with our website.
                </p>
              </div>
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4"
                  checked={consentState.analytics}
                  onChange={() => handleConsentChange('analytics')}
                  aria-label="Analytics cookies"
                />
              </div>
            </div>
            
            <div className="text-xs text-muted-foreground mt-2">
              For more information, please visit our{' '}
              <Link to="/privacy" className="text-primary underline">
                Privacy Policy
              </Link>.
            </div>
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={acceptNecessary}>
              Necessary Only
            </Button>
            <Button variant="default" onClick={acceptAll}>
              Accept All
            </Button>
            <Button
              variant="secondary"
              onClick={() => saveConsent(consentState)}
            >
              Save Preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Fixed button to open cookie settings */}
      <div className="fixed bottom-4 left-4 z-50">
        <Button
          variant="outline"
          size="sm"
          className="bg-background shadow-md border"
          onClick={openPreferences}
        >
          Cookie Settings
        </Button>
      </div>
    </>
  );
};

export default CookieConsent;
